import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, Link, useParams } from 'react-router-dom';
import { LayoutDashboard, Database, Scale, Menu, Users, LogOut, Monitor, Bell, GraduationCap, ChevronLeft, ChevronRight, Settings, CreditCard, Sparkles, HelpCircle, Shield } from 'lucide-react';
import { AppSettings, GovernanceTask, Tool, Employee, Notification, Feedback } from './types';

import { useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { InternalSessionGuard } from './components/InternalSessionGuard';

import { useTools } from './hooks/useTools';
import { useEmployees } from './hooks/useEmployees';
import { useNotifications } from './hooks/useNotifications';
import { useGovernance } from './hooks/useGovernance';
import { useTraining } from './hooks/useTraining';
import { useSettings } from './hooks/useSettings';
import { useAudit } from './hooks/useAudit';
import { useBilling } from './hooks/useBilling';
import { usePaddle } from './hooks/usePaddle';
import { useInitialAppData } from './hooks/useInitialAppData';
import { checkToolLimit, getAdvisorEnabledFlag } from './services/storageService';
import { sendAccessRequestAdminEmail, sendTrainingReminderEmail } from './services/emailService';
import { ToolLimitResult } from './types';
import {
  createAccessProvisionNotification,
  createRevokeAccessNotification,
  hasPendingAccessTaskFor,
  hasPendingRevokeTaskFor,
  mergeMissingTags,
  syncToolAssignmentsFromTeams,
} from './services/appOrchestration';

import { DashboardView } from './views/DashboardView';
import { InventoryView } from './views/InventoryView';
import { GovernanceView } from './views/GovernanceView';
import { EmployeeView } from './views/EmployeeView';
import { UserPortalView } from './views/UserPortalView';
import { NotificationCenterView } from './views/NotificationCenterView';
import { TrainingScoresView } from './views/TrainingScoresView';
import { ConfigurationView } from './views/ConfigurationView';
import { LoginView } from './views/LoginView';
import { InternalLoginView } from './views/InternalLoginView';
import { ForgotPasswordView } from './views/ForgotPasswordView';
import { SetPasswordView } from './views/SetPasswordView';
import { AdminSignupView } from './views/AdminSignupView';
import { OnboardingView } from './views/OnboardingView';
import BillingView from './views/BillingView';
import { HelpView } from './views/HelpView';
import { InternalTenantDetailView } from './views/InternalTenantDetailView';
import { InternalTenantBillingActionsView } from './views/InternalTenantBillingActionsView';
import { InternalTenantsOverviewView } from './views/InternalTenantsOverviewView';
import { UserHelpView } from './views/UserHelpView';
import { ActivationChecklist } from './components/ActivationChecklist';
import { ReadOnlyBanner } from './components/ReadOnlyBanner';
import { AdvisorPanel } from './components/AdvisorPanel';
import { isInternalOperatorSessionActive } from './services/internalSession';

type ViewMode = 'admin' | 'portal';

const OasixLogo = ({ className = "w-8 h-8" }: { className?: string }) => (
  <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="10" strokeLinecap="round" className={className}>
    <ellipse cx="50" cy="80" rx="35" ry="8" />
    <path d="M20 55 C 20 25, 80 25, 80 55" />
    <path d="M35 55 C 35 40, 65 40, 65 55" />
  </svg>
);

const InternalTenantDetailRoute: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  if (!companyId) return <Navigate to="/internal/tenants" replace />;
  return <InternalTenantDetailView companyId={companyId} />;
};

const InternalTenantBillingActionsRoute: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  if (!companyId) return <Navigate to="/internal/tenants" replace />;
  return <InternalTenantBillingActionsView />;
};

const App: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const { isAuthenticated, isAdmin, employee: authEmployee, companyId, signOut: doSignOut, loading: authLoading } = useAuth();
  const viewMode: ViewMode = pathname.startsWith('/portal') ? 'portal' : 'admin';
  const portalUser = authEmployee;
  const isSuperAdmin = authEmployee?.authRole === 'super_admin';
  const hasInternalOperatorSession = isInternalOperatorSessionActive();

  // --- Data hooks ---
  const toolsHook = useTools(companyId);
  const employeesHook = useEmployees(companyId);
  const notificationsHook = useNotifications(companyId);
  const governanceHook = useGovernance(companyId);
  const trainingHook = useTraining(companyId);
  const settingsHook = useSettings(companyId);
  const auditHook = useAudit(companyId);
  const billingHook = useBilling(companyId);
  const { openCheckout, openPortal } = usePaddle();

  // UI States
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const [toolLimit, setToolLimit] = useState<ToolLimitResult | null>(null);
  const [isAdvisorOpen, setIsAdvisorOpen] = useState(false);
  const [advisorEnabledForTenant, setAdvisorEnabledForTenant] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadAdvisorFlag = async () => {
      if (!companyId) return;
      try {
        const enabled = await getAdvisorEnabledFlag(companyId);
        if (!cancelled) {
          setAdvisorEnabledForTenant(enabled);
          if (!enabled) setIsAdvisorOpen(false);
        }
      } catch {
        if (!cancelled) setAdvisorEnabledForTenant(true);
      }
    };
    loadAdvisorFlag();
    return () => { cancelled = true; };
  }, [companyId]);

  useInitialAppData({
    companyId,
    loadTools: toolsHook.load,
    loadEmployees: employeesHook.load,
    loadNotifications: notificationsHook.load,
    loadTraining: trainingHook.load,
    loadSettings: settingsHook.load,
    loadAudit: auditHook.load,
    loadBilling: billingHook.load,
    loadGovernance: governanceHook.load,
    syncGovernanceFromTools: governanceHook.syncFromTools,
  });

  const showSidebarContent = !isCollapsed || isSidebarHovered;

  // ================================================
  // Orchestration helpers (cross-entity logic)
  // ================================================

  const addRevokeNotificationsForRemoved = async (previousIds: string[], newIds: string[], tool: Tool) => {
    const newSet = new Set(newIds || []);
    const removed = (previousIds || []).filter((id) => !newSet.has(id));
    for (const employeeId of removed) {
      const emp = employeesHook.employees.find((e) => e.id === employeeId);
      if (!emp || hasPendingRevokeTaskFor(notificationsHook.notifications, tool.id, employeeId)) continue;
      await notificationsHook.add(createRevokeAccessNotification(emp, tool));
    }
  };

  const addProvisionNotificationsForNew = async (previousIds: string[], newIds: string[], tool: Tool) => {
    const prevSet = new Set(previousIds || []);
    const newlyAdded = (newIds || []).filter((id) => !prevSet.has(id));
    for (const employeeId of newlyAdded) {
      const emp = employeesHook.employees.find((e) => e.id === employeeId);
      if (!emp || hasPendingAccessTaskFor(notificationsHook.notifications, tool.id, employeeId)) continue;
      await notificationsHook.add(createAccessProvisionNotification(emp, tool));
    }
  };

  const mergeTagsToSettings = async (toolTags: string[]) => {
    const merged = mergeMissingTags(settingsHook.settings.tags ?? [], toolTags);
    if (merged.length !== (settingsHook.settings.tags ?? []).length) {
      await settingsHook.save({ ...settingsHook.settings, tags: merged });
    }
  };

  // ================================================
  // 1. TOOL ACTIONS
  // ================================================

  const refreshToolLimit = async (): Promise<ToolLimitResult | null> => {
    if (!companyId) return null;
    const result = await checkToolLimit(companyId);
    setToolLimit(result);
    return result;
  };

  const handleAddTool = async (tool: Tool) => {
    if (!companyId) return;
    if (billingHook.isReadOnly) { alert('Your account is in read-only mode. Subscribe to add tools.'); return; }
    const limit = await refreshToolLimit();
    if (limit && !limit.allowed) return;
    const toolWithAssignments = syncToolAssignmentsFromTeams(tool, employeesHook.employees);
    const updated = await toolsHook.add(toolWithAssignments);
    await addProvisionNotificationsForNew([], toolWithAssignments.assignedUserIds || [], toolWithAssignments);
    const currentGov = await governanceHook.load();
    await governanceHook.syncFromTools(updated, Array.isArray(currentGov) ? currentGov : [], settingsHook.settings.criticalityLevels);
    await mergeTagsToSettings(toolWithAssignments.tags || []);
    await refreshToolLimit();
  };

  const handleUpdateTool = async (tool: Tool) => {
    if (!companyId) return;
    if (billingHook.isReadOnly) { alert('Your account is in read-only mode. Subscribe to edit tools.'); return; }
    const toolWithAssignments = syncToolAssignmentsFromTeams(tool, employeesHook.employees);
    const previousTool = toolsHook.tools.find((t) => t.id === tool.id);
    const previousIds = previousTool?.assignedUserIds || [];
    const updated = await toolsHook.update(toolWithAssignments);
    await addProvisionNotificationsForNew(previousIds, toolWithAssignments.assignedUserIds || [], toolWithAssignments);
    await addRevokeNotificationsForRemoved(previousIds, toolWithAssignments.assignedUserIds || [], toolWithAssignments);
    const currentGov = await governanceHook.load();
    await governanceHook.syncFromTools(updated, Array.isArray(currentGov) ? currentGov : [], settingsHook.settings.criticalityLevels);
    await mergeTagsToSettings(toolWithAssignments.tags || []);
  };

  const handleDeleteTool = async (id: string) => {
    if (billingHook.isReadOnly) { alert('Your account is in read-only mode.'); return; }
    if (window.confirm('Are you sure you want to delete this tool?')) {
      await toolsHook.remove(id);
    }
  };

  const handleAddGovernanceTask = async (task: GovernanceTask) => {
    await governanceHook.add(task);
  };

  const handleToggleGovernanceTask = async (id: string) => {
    const previousTask = governanceHook.tasks.find((task) => task.id === id);
    const updated = await governanceHook.toggle(id);
    const toggled = updated.find((task) => task.id === id);

    // Track completions in audit trail while keeping the task hidden from active lists.
    if (previousTask && toggled?.isCompleted && !previousTask.isCompleted) {
      const relatedTool = toggled.toolId ? toolsHook.tools.find((tool) => tool.id === toggled.toolId) : undefined;
      await auditHook.addEntry({
        eventType: 'GOVERNANCE_TASK_COMPLETED',
        message: `Governance task completed: ${toggled.title}`,
        toolName: relatedTool?.name ?? undefined,
        newStatus: 'DONE',
      });
    }

    return updated;
  };

  // ================================================
  // 2. EMPLOYEE ACTIONS
  // ================================================

  const syncEmployeeToToolsByTeam = async (emp: Employee) => {
    if (!emp.team) return;
    const toolsToUpdate = toolsHook.tools.filter(
      (t) => (t.teams || []).includes(emp.team!) && !(t.assignedUserIds || []).includes(emp.id)
    );
    for (const tool of toolsToUpdate) {
      const updatedTool = { ...tool, assignedUserIds: [...(tool.assignedUserIds || []), emp.id] };
      await toolsHook.update(updatedTool);
      if (!hasPendingAccessTaskFor(notificationsHook.notifications, tool.id, emp.id)) {
        await notificationsHook.add(createAccessProvisionNotification(emp, tool));
      }
    }
  };

  const handleAddEmployee = async (emp: Employee) => {
    if (billingHook.isReadOnly) { alert('Your account is in read-only mode.'); return; }
    const updated = await employeesHook.add(emp);
    const added = updated.find((e: Employee) => e.id === emp.id);
    if (added) await syncEmployeeToToolsByTeam(added);
  };

  const handleUpdateEmployee = async (emp: Employee) => {
    const updated = await employeesHook.update(emp);
    const updatedEmp = updated.find((e: Employee) => e.id === emp.id);
    if (updatedEmp) await syncEmployeeToToolsByTeam(updatedEmp);
  };

  const handleDeleteEmployee = async (id: string) => {
    if (window.confirm('Are you sure? This will remove them from any tools they are assigned to.')) {
      const emp = employeesHook.employees.find((e) => e.id === id);
      const toolsAssignedTo = emp ? toolsHook.tools.filter((t) => t.assignedUserIds?.includes(emp.id)) : [];
      for (const tool of toolsAssignedTo) {
        if (!emp || hasPendingRevokeTaskFor(notificationsHook.notifications, tool.id, emp.id)) continue;
        await notificationsHook.add(createRevokeAccessNotification(emp, tool));
      }
      await employeesHook.remove(id);
      await toolsHook.load();
    }
  };

  // ================================================
  // 3. NOTIFICATION & TRAINING ACTIONS
  // ================================================

  const handleAddNotification = async (notif: Notification) => {
    await notificationsHook.add(notif);
    if (notif.type === 'ACCESS_REQUEST' && notif.status === 'PENDING') {
      // Email delivery should not block in-app notification creation.
      sendAccessRequestAdminEmail(notif).catch((error) => {
        console.error('Failed to send access request admin email:', error);
      });
    }
  };

  const handleUpdateNotification = async (notif: Notification) => {
    await notificationsHook.update(notif);
  };

  const closedStatuses = ['DONE', 'ABORTED', 'REJECTED', 'APPROVED', 'ACKNOWLEDGED', 'FORWARDED'];
  const handleAccessTaskClosed = async (notif: Notification, newStatus: string) => {
    if (!closedStatuses.includes(newStatus)) return;
    const message = `Access task closed: ${notif.toolName || 'Request'} — ${notif.requesterName || 'Requester'} — ${newStatus}`;
    await auditHook.addEntry({
      eventType: 'ACCESS_TASK_CLOSED',
      message,
      notificationId: notif.id,
      toolName: notif.toolName ?? undefined,
      requesterName: notif.requesterName ?? undefined,
      newStatus,
    });
  };

  const handleToolFeedback = async (toolId: string, satisfaction: number, proficiency: number, feedbackText: string, isTrainingCompletion: boolean = false) => {
    if (!portalUser) return;
    const tool = toolsHook.tools.find(t => t.id === toolId);
    if (!tool) return;

    const newFeedback: Feedback = {
      id: crypto.randomUUID(),
      userId: portalUser.id,
      userName: portalUser.name,
      satisfaction,
      proficiency,
      comment: feedbackText,
      date: new Date().toISOString(),
      type: satisfaction <= 4 ? 'COMPLAINT' : 'GENERAL',
      status: 'OPEN',
    };

    const currentFeedback = tool.feedback || [];
    const updatedFeedbacks = [...currentFeedback, newFeedback];

    const latestFeedbackMap = new Map<string, Feedback>();
    updatedFeedbacks.forEach(f => latestFeedbackMap.set(f.userId, f));
    const uniqueFeedbacks = Array.from(latestFeedbackMap.values());
    const n = uniqueFeedbacks.length;
    const avgSat = n > 0 ? uniqueFeedbacks.reduce((acc, f) => acc + (f.satisfaction ?? 0), 0) / n : 0;
    const avgProf = n > 0 ? uniqueFeedbacks.reduce((acc, f) => acc + (f.proficiency ?? 0), 0) / n : 0;

    const updatedTool = {
      ...tool,
      feedback: updatedFeedbacks,
      satisfactionScore: Math.round(avgSat * 10),
      teamLiteracy: Number(avgProf.toFixed(1)),
    };

    await handleUpdateTool(updatedTool);

    if (isTrainingCompletion && trainingHook.curricula.filter((c) => c.toolId === toolId).length === 0) {
      await trainingHook.completeRecord(portalUser.id, toolId, satisfaction, proficiency);
      const newNotif: Notification = {
        id: crypto.randomUUID(),
        type: 'TRAINING_COMPLETED',
        status: 'PENDING',
        requesterId: portalUser.id,
        requesterName: portalUser.name,
        createdAt: new Date().toISOString(),
        toolId: tool.id,
        toolName: tool.name,
      };
      await handleAddNotification(newNotif);
    }
  };

  const handleCurriculumCompletion = async (employeeId: string, curriculumId: string, satisfaction: number) => {
    await trainingHook.addCompletion(employeeId, curriculumId, satisfaction);
    const curriculum = (trainingHook.curricula || []).find((c) => c.id === curriculumId);
    const tool = curriculum ? toolsHook.tools.find((t) => t.id === curriculum.toolId) : null;
    if (tool && portalUser?.id === employeeId) {
      const newNotif: Notification = {
        id: crypto.randomUUID(),
        type: 'TRAINING_COMPLETED',
        status: 'PENDING',
        requesterId: employeeId,
        requesterName: portalUser.name,
        createdAt: new Date().toISOString(),
        toolId: tool.id,
        toolName: tool.name,
      };
      await notificationsHook.add(newNotif);
    }
  };

  const handleRequestAccess = async (toolId: string, reason: string, neededBy: string, mode: 'new' | 'change' = 'new') => {
    if (!portalUser) return;
    const tool = toolsHook.tools.find(t => t.id === toolId);
    if (!tool) return;
    await handleAddNotification({
      id: crypto.randomUUID(),
      type: 'ACCESS_REQUEST',
      status: 'PENDING',
      requesterId: portalUser.id,
      requesterName: portalUser.name,
      createdAt: new Date().toISOString(),
      toolId: tool.id,
      toolName: tool.name,
      accessReason: reason,
      accessNeededBy: neededBy,
      requestKind: mode,
    });
  };

  const handleRequestTraining = async (toolId: string, needs: string) => {
    if (!portalUser) return;
    const tool = toolsHook.tools.find(t => t.id === toolId);
    if (!tool) return;
    await handleAddNotification({
      id: crypto.randomUUID(),
      type: 'TRAINING_REQUEST',
      status: 'PENDING',
      requesterId: portalUser.id,
      requesterName: portalUser.name,
      createdAt: new Date().toISOString(),
      toolId: tool.id,
      toolName: tool.name,
      trainingNeeds: needs,
    });
  };

  const handleSendTrainingReminder = async (employeeId: string, _employeeName: string, _employeeEmail: string, curriculumName: string, toolName: string) => {
    await handleAddNotification({
      id: crypto.randomUUID(),
      type: 'OWNER_TASK',
      status: 'PENDING',
      requesterId: 'system',
      requesterName: 'Training System',
      toolName,
      message: `Reminder: Please complete your training "${curriculumName}" for ${toolName}.`,
      createdAt: new Date().toISOString(),
      targetUserId: employeeId,
    });
    await sendTrainingReminderEmail({ employeeId, curriculumName, toolName });
  };

  const handleRequestNewTool = async (data: Notification['newToolData']) => {
    if (!portalUser) return;
    await handleAddNotification({
      id: crypto.randomUUID(),
      type: 'NEW_TOOL_REQUEST',
      status: 'PENDING',
      requesterId: portalUser.id,
      requesterName: portalUser.name,
      createdAt: new Date().toISOString(),
      newToolData: data,
    });
  };

  // ================================================
  // 4. SETTINGS
  // ================================================

  const handleUpdateSettings = async (newSettings: AppSettings) => {
    await settingsHook.save(newSettings);
  };

  const settingsTab: 'settings' | 'billing' = (() => {
    const params = new URLSearchParams(location.search);
    return params.get('tab') === 'billing' || params.get('billing') === 'upgrade' ? 'billing' : 'settings';
  })();

  const setSettingsTab = (tab: 'settings' | 'billing') => {
    const params = new URLSearchParams(location.search);
    if (tab === 'billing') params.set('tab', 'billing');
    else params.delete('tab');
    // Legacy query used by older links/buttons.
    params.delete('billing');
    navigate({
      pathname: '/admin/settings',
      search: params.toString() ? `?${params.toString()}` : '',
    });
  };

  const renderBillingPanel = () => (
    <BillingView
      billing={billingHook.billing}
      usage={billingHook.usage}
      trialDaysLeft={billingHook.trialDaysLeft}
      loading={billingHook.loading}
      onSetSpendingCap={billingHook.setSpendingCap}
      onOpenCheckout={(plan, cycle) => {
        if (!authEmployee?.email || !companyId) return;
        openCheckout({
          plan,
          billing: cycle,
          email: authEmployee.email,
          companyId,
          onSuccess: () => {
            billingHook.load();
            navigate('/admin/settings?tab=billing');
          },
        });
      }}
      onOpenPortal={async () => {
        if (!companyId) return;
        try {
          await openPortal(companyId);
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Unable to open customer portal.';
          alert(msg);
        }
      }}
    />
  );

  // ================================================
  // RENDER
  // ================================================

  const renderContent = () => {
    if (pathname.startsWith('/portal')) {
      return (
        <Routes>
          <Route
            path="/portal"
            element={
              <ProtectedRoute>
                {portalUser ? (
                  <UserPortalView
                    currentUser={portalUser}
                    tools={toolsHook.tools}
                    employees={employeesHook.employees}
                    curricula={trainingHook.curricula}
                    curriculumCompletions={trainingHook.completions}
                    onCompleteCurriculum={handleCurriculumCompletion}
                    onSubmitFeedback={handleToolFeedback}
                    onRequestAccess={handleRequestAccess}
                    onRequestNewTool={handleRequestNewTool}
                    onRequestTraining={handleRequestTraining}
                    trainingRecords={trainingHook.trainingRecords}
                    onUpdateTool={handleUpdateTool}
                    notifications={notificationsHook.notifications}
                    onUpdateNotification={handleUpdateNotification}
                    onAccessTaskClosed={handleAccessTaskClosed}
                  />
                ) : (
                  <div className="p-8 text-center text-slate-500">No employee account linked to your login.</div>
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/portal/help"
            element={
              <ProtectedRoute>
                <UserHelpView />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/portal" replace />} />
        </Routes>
      );
    }

    if (!settingsHook.settingsLoaded && pathname.startsWith('/admin/onboarding')) {
      return (
        <div className="flex items-center justify-center h-screen bg-slate-50">
          <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    if (needsOnboarding && !pathname.startsWith('/admin/onboarding')) {
      return <Navigate to="/admin/onboarding" replace />;
    }

    if (settingsHook.settingsLoaded && !needsOnboarding && pathname.startsWith('/admin/onboarding')) {
      return <Navigate to="/admin/dashboard" replace />;
    }

    return (
      <Routes>
        <Route path="/admin/onboarding" element={
          <ProtectedRoute requiredRole="admin">
            <OnboardingView
              settings={settingsHook.settings}
              onUpdateSettings={handleUpdateSettings}
              companyId={companyId}
              onAddTool={handleAddTool}
              onReloadSettings={settingsHook.load}
            />
          </ProtectedRoute>
        } />
        <Route path="/admin/dashboard" element={
          <ProtectedRoute requiredRole="admin">
            <DashboardView
              tools={toolsHook.tools}
              employees={employeesHook.employees}
              notifications={notificationsHook.notifications}
              trainingRecords={trainingHook.trainingRecords}
              governanceTasks={governanceHook.tasks}
              curricula={trainingHook.curricula}
              curriculumCompletions={trainingHook.completions}
              settings={settingsHook.settings}
              onNavigateToNotifications={() => navigate('/admin/notifications')}
            />
          </ProtectedRoute>
        } />
        <Route path="/admin/inventory" element={
          <ProtectedRoute requiredRole="admin">
            <InventoryView
              tools={toolsHook.tools}
              employees={employeesHook.employees}
              onAdd={handleAddTool}
              onUpdate={handleUpdateTool}
              onDelete={handleDeleteTool}
              onAddGovernanceTask={handleAddGovernanceTask}
              curricula={trainingHook.curricula}
              settings={settingsHook.settings}
              onCheckToolLimit={refreshToolLimit}
              toolLimit={toolLimit}
              onUpgrade={() => navigate('/admin/settings?billing=upgrade')}
            />
          </ProtectedRoute>
        } />
        <Route path="/admin/employees" element={
          <ProtectedRoute requiredRole="admin">
            <EmployeeView
              employees={employeesHook.employees}
              tools={toolsHook.tools}
              onAdd={handleAddEmployee}
              onUpdate={handleUpdateEmployee}
              onDelete={handleDeleteEmployee}
              onUpdateTool={handleUpdateTool}
              trainingRecords={trainingHook.trainingRecords}
              settings={settingsHook.settings}
              notifications={notificationsHook.notifications}
            />
          </ProtectedRoute>
        } />
        <Route path="/admin/notifications" element={
          <ProtectedRoute requiredRole="admin">
            <NotificationCenterView
              notifications={notificationsHook.notifications}
              onNotificationsChange={notificationsHook.setNotifications}
              onUpdateNotification={handleUpdateNotification}
              tools={toolsHook.tools}
              employees={employeesHook.employees}
              onAddNotification={handleAddNotification}
              onAccessTaskClosed={handleAccessTaskClosed}
            />
          </ProtectedRoute>
        } />
        <Route path="/admin/learning" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/admin/governance" element={
          <ProtectedRoute requiredRole="admin">
            <GovernanceView
              tools={toolsHook.tools}
              settings={settingsHook.settings}
              governanceTasks={governanceHook.tasks}
              onUpdateTool={handleUpdateTool}
              onAddTask={governanceHook.add}
              onToggleTask={handleToggleGovernanceTask}
              onDeleteTask={governanceHook.remove}
              onReloadTasks={governanceHook.load}
            />
          </ProtectedRoute>
        } />
        <Route path="/admin/training" element={
          <ProtectedRoute requiredRole="admin">
            <TrainingScoresView
              trainingRecords={trainingHook.trainingRecords}
              curriculumCompletions={trainingHook.completions}
              tools={toolsHook.tools}
              employees={employeesHook.employees}
              curricula={trainingHook.curricula}
              onAddCurriculum={trainingHook.addCurriculum}
              onUpdateCurriculum={trainingHook.updateCurriculum}
              onDeleteCurriculum={trainingHook.deleteCurriculum}
              onUpdateTool={handleUpdateTool}
              onSendReminder={handleSendTrainingReminder}
            />
          </ProtectedRoute>
        } />
        <Route path="/admin/settings" element={
          <ProtectedRoute requiredRole="admin">
            <div className="space-y-6 animate-fade-in">
              <div className="flex space-x-1 bg-slate-200 p-1 rounded-xl w-fit">
                <button
                  type="button"
                  onClick={() => setSettingsTab('settings')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                    settingsTab === 'settings' ? 'bg-toggle text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Settings size={16} /> Settings
                </button>
                <button
                  type="button"
                  onClick={() => setSettingsTab('billing')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                    settingsTab === 'billing' ? 'bg-toggle text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <CreditCard size={16} /> Billing
                </button>
              </div>

              {settingsTab === 'settings' ? (
                <ConfigurationView
                  settings={settingsHook.settings}
                  onUpdateSettings={handleUpdateSettings}
                />
              ) : (
                renderBillingPanel()
              )}
            </div>
          </ProtectedRoute>
        } />
        <Route path="/admin/billing" element={
          <ProtectedRoute requiredRole="admin">
            <Navigate to="/admin/settings?tab=billing" replace />
          </ProtectedRoute>
        } />
        <Route path="/admin/help" element={
          <ProtectedRoute requiredRole="admin">
            <HelpView />
          </ProtectedRoute>
        } />
        <Route path="/internal/tenants" element={
          <InternalSessionGuard>
            <InternalTenantsOverviewView />
          </InternalSessionGuard>
        } />
        <Route path="/internal/tenants/:companyId" element={
          <InternalSessionGuard>
            <InternalTenantDetailRoute />
          </InternalSessionGuard>
        } />
        <Route path="/internal/tenants/:companyId/billing-actions" element={
          <InternalSessionGuard>
            <InternalTenantBillingActionsRoute />
          </InternalSessionGuard>
        } />
        <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="*" element={<Navigate to={isAdmin ? '/admin/dashboard' : '/portal'} replace />} />
      </Routes>
    );
  };

  const NavItem = ({ to, label, icon: Icon, badge, testId }: { to: string; label: string; icon: any; badge?: number; testId?: string }) => {
    const isActive = pathname === to || (to !== '/admin' && pathname.startsWith(to));
    return (
      <Link
        to={to}
        data-testid={testId}
        onClick={() => setIsSidebarOpen(false)}
        className={`w-full flex items-center px-4 py-3 rounded-lg transition-all duration-200 font-medium whitespace-nowrap overflow-hidden
          ${isActive ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}
          ${!showSidebarContent ? 'justify-center' : 'justify-between'}
        `}
        title={!showSidebarContent ? label : undefined}
      >
        <div className={`flex items-center gap-3 ${!showSidebarContent ? 'justify-center w-full' : ''}`}>
          <div className="relative">
            <Icon size={20} className="flex-shrink-0" />
            {badge && badge > 0 && !showSidebarContent && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-slate-900" />
            )}
          </div>
          <span className={`transition-opacity duration-300 ${!showSidebarContent ? 'opacity-0 w-0 hidden' : 'opacity-100'}`}>
            {label}
          </span>
        </div>
        {badge && badge > 0 && showSidebarContent && (
          <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{badge}</span>
        )}
      </Link>
    );
  };

  const needsOnboarding = isAuthenticated && isAdmin && settingsHook.settingsLoaded && settingsHook.settings.onboardingCompleted === false;

  const publicRoutes = ['/login', '/internal/login', '/signup', '/forgot-password', '/set-password'];
  if (publicRoutes.some(r => pathname.startsWith(r))) {
    return (
      <Routes>
        <Route path="/login" element={<LoginView />} />
        <Route path="/internal/login" element={<InternalLoginView />} />
        <Route path="/signup" element={<AdminSignupView />} />
        <Route path="/forgot-password" element={<ForgotPasswordView />} />
        <Route path="/set-password" element={<SetPasswordView />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    if (pathname.startsWith('/internal/')) {
      return <Navigate to="/internal/login" replace />;
    }
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <aside
        onMouseEnter={() => setIsCollapsed(prev => { if(prev) setIsSidebarHovered(true); return prev; })}
        onMouseLeave={() => setIsSidebarHovered(false)}
        className={`
        fixed inset-y-0 left-0 z-30 bg-slate-900 text-white transform transition-all duration-300 ease-in-out lg:relative flex flex-col shadow-xl
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${!showSidebarContent ? 'lg:w-20' : 'lg:w-64'}
      `}>
        <div className={`p-6 border-b border-slate-800 flex items-center ${!showSidebarContent ? 'justify-center' : 'justify-between'} gap-3 min-h-[88px]`}>
          <div className="flex items-center gap-3 overflow-hidden">
            <div className="bg-indigo-500 p-2 rounded-lg text-white flex-shrink-0">
              <OasixLogo className="w-6 h-6" />
            </div>
            <div className={`transition-opacity duration-300 ${!showSidebarContent ? 'opacity-0 w-0 hidden' : 'opacity-100'}`}>
              <h1 className="text-xl font-bold tracking-tight text-white whitespace-nowrap">Oasix</h1>
              <p className="text-xs text-slate-400 whitespace-nowrap">Digital Oasis</p>
            </div>
          </div>

          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={`hidden lg:flex p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition-colors ${!showSidebarContent ? 'absolute -right-12 top-8 hidden' : ''}`}
          >
            {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        {!showSidebarContent && (
          <button
            onClick={() => setIsCollapsed(false)}
            className="hidden lg:flex mx-auto mt-2 p-1 text-slate-500 hover:text-white"
          >
            <ChevronRight size={16} />
          </button>
        )}

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto scrollbar-thin">
          {viewMode === 'admin' && isAdmin ? (
            <>
              <NavItem to="/admin/dashboard" label="Dashboard" icon={LayoutDashboard} />
              <NavItem to="/admin/notifications" label="Notifications" icon={Bell} />
              <div className="my-2 border-t border-slate-800" />
              <NavItem to="/admin/governance" label="Governance" icon={Scale} />
              <div className="my-2 border-t border-slate-800" />
              <NavItem to="/admin/training" label="Training" icon={GraduationCap} />
              <div className="my-2 border-t border-slate-800" />
              <NavItem to="/admin/inventory" label="Tool Database" icon={Database} />
              <NavItem to="/admin/employees" label="Team Directory" icon={Users} />
              <div className="my-2 border-t border-slate-800" />
              <NavItem to="/admin/settings" label="Settings" icon={Settings} testId="nav-settings" />
              <div className="my-2 border-t border-slate-800" />
              <button
                onClick={() => {
                  if (!advisorEnabledForTenant) {
                    alert('Advisor is temporarily disabled for this tenant.');
                    return;
                  }
                  setIsAdvisorOpen(true);
                }}
                className={`w-full flex items-center px-4 py-3 rounded-lg transition-all duration-200 font-medium whitespace-nowrap overflow-hidden text-slate-300 hover:bg-indigo-600/20 hover:text-indigo-300 ${!showSidebarContent ? 'justify-center' : ''}`}
                title="Oasix Advisor"
              >
                <div className={`flex items-center gap-3 ${!showSidebarContent ? 'justify-center w-full' : ''}`}>
                  <Sparkles size={20} className="flex-shrink-0 text-amber-400" />
                  <span className={`transition-opacity duration-300 ${!showSidebarContent ? 'opacity-0 w-0 hidden' : 'opacity-100'}`}>
                    Advisor
                  </span>
                </div>
              </button>
              <NavItem to="/admin/help" label="Help" icon={HelpCircle} />
              {isSuperAdmin && hasInternalOperatorSession && (
                <NavItem to="/internal/tenants" label="Internal CP" icon={Shield} />
              )}
            </>
          ) : (
            <>
              <NavItem to="/portal" label="My Portal" icon={Users} />
              <NavItem to="/portal/help" label="Help" icon={HelpCircle} />
            </>
          )}
        </nav>

        <div className="p-4 border-t border-slate-800 bg-slate-900">
          {isAdmin ? (
            <div className={`bg-slate-800 rounded-lg p-1 flex mb-4 transition-all ${!showSidebarContent ? 'flex-col gap-2' : ''}`}>
              <Link
                to="/admin/dashboard"
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-xs font-bold transition-all ${viewMode === 'admin' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'}`}
                title="Admin View"
              >
                <Monitor size={14} /> {showSidebarContent && 'Admin'}
              </Link>
              <Link
                to="/portal"
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-xs font-bold transition-all ${viewMode === 'portal' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'}`}
                title="Portal View"
              >
                <Users size={14} /> {showSidebarContent && 'Portal'}
              </Link>
            </div>
          ) : (
            <div className="mb-4" />
          )}

          <div className={`flex items-center gap-3 px-2 transition-all ${!showSidebarContent ? 'justify-center' : ''}`}>
            <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center font-bold text-slate-300 flex-shrink-0">
              {authEmployee?.name?.charAt(0)?.toUpperCase() || '?'}
            </div>

            <div className={`overflow-hidden transition-all duration-300 ${!showSidebarContent ? 'opacity-0 w-0 hidden' : 'opacity-100'}`}>
              <p className="text-sm font-medium truncate text-white">
                {authEmployee?.name || 'User'}
              </p>
              <p className="text-xs text-slate-400 truncate">
                {isAdmin ? 'Administrator' : 'Employee'}
              </p>
            </div>

            {showSidebarContent && (
              <button
                onClick={() => doSignOut().then(() => navigate('/login'))}
                className="ml-auto text-slate-500 hover:text-white"
                title="Sign out"
              >
                <LogOut size={16} />
              </button>
            )}
          </div>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden transition-all duration-300">
        <div className="lg:hidden bg-white border-b border-slate-200 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 font-bold text-slate-800">
            <div className="text-indigo-600">
              <OasixLogo className="w-6 h-6" />
            </div>
            Oasix
          </div>
          <button onClick={() => setIsSidebarOpen(true)} className="text-slate-600">
            <Menu size={24} />
          </button>
        </div>

        {billingHook.isReadOnly && billingHook.billing && (
          <ReadOnlyBanner
            plan={billingHook.billing.plan as 'expired' | 'cancelled'}
            trialDaysLeft={billingHook.trialDaysLeft}
          />
        )}

        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-8">
          <div className="max-w-7xl mx-auto">
            {viewMode === 'admin' && (
              ['/admin/dashboard', '/admin/notifications', '/admin/inventory', '/admin/governance', '/admin/training', '/admin/employees', '/admin/help'].includes(pathname)
              || pathname.startsWith('/internal/tenants')
            ) && (
              <header className="mb-8 hidden lg:block">
                <h1 className="text-2xl font-bold text-slate-800">
                  {pathname === '/admin/dashboard' && 'Executive Dashboard'}
                  {pathname === '/admin/notifications' && 'Notification Center'}
                  {pathname === '/admin/inventory' && 'Technology Stack Inventory'}
                  {pathname === '/admin/governance' && 'Governance & Renewals'}
                  {pathname === '/admin/training' && 'Training'}
                  {pathname === '/admin/employees' && 'Team Directory'}
                  {pathname === '/admin/help' && 'Help Center'}
                  {pathname.startsWith('/internal/tenants') && 'Internal Control Plane'}
                </h1>
                <p className="text-slate-500 mt-1">
                  {pathname === '/admin/dashboard' && 'Stop wandering the SaaS desert. Welcome to Oasix, where your tech stack finally makes sense.'}
                  {pathname === '/admin/notifications' && 'Review incoming requests for access and new tools.'}
                  {pathname === '/admin/inventory' && 'Manage your complete list of software tools, pricing models, and ownership.'}
                  {pathname === '/admin/governance' && 'Monitor upcoming contract renewals and notice deadlines.'}
                  {pathname === '/admin/training' && 'Track training completion rates and competency scores.'}
                  {pathname === '/admin/employees' && 'Your team members and their tool assignments, training status, and roles.'}
                  {pathname === '/admin/help' && 'Find the next best action and role-specific operational guides.'}
                  {pathname.startsWith('/internal/tenants') && 'Super-admin tenant diagnostics in read-only mode.'}
                </p>
              </header>
            )}

            {viewMode === 'admin' && pathname.startsWith('/admin') && !pathname.startsWith('/admin/onboarding') && (
              <ActivationChecklist
                tools={toolsHook.tools}
                employees={employeesHook.employees}
                curricula={trainingHook.curricula}
                governanceTasks={governanceHook.tasks}
                settings={settingsHook.settings}
                onUpdateSettings={handleUpdateSettings}
              />
            )}

            {renderContent()}
          </div>
        </div>
      </main>
      <AdvisorPanel
        isOpen={isAdvisorOpen}
        onClose={() => setIsAdvisorOpen(false)}
        tools={toolsHook.tools}
        plan={(billingHook.billing?.plan ?? 'trial') as any}
        companyId={companyId}
      />
    </div>
  );
};

export default App;
